import { t } from 'i18next';
import { Params } from 'react-router-dom';
import { UrlHelper } from '../../common/utils/UrlHelper';
import { PATHS, Route, TabNameLocalizationKeys } from '../../routing/routes';
import { Breadcrumb } from '../models/Breadcrumb';

type BreadcrumbsContext = {
  nestedTabs?: Route[];
};

type ProjectBreadcrumbsContext = BreadcrumbsContext & {
  projectId: string;
  projectLabel: string;
};

type KeyBreadcrumbsContext = BreadcrumbsContext & {
  keyId: string;
};

type ProfileBreadcrumbsContext = BreadcrumbsContext & {
  path: string;
  label: string;
};

type CertificateBreadcrumbsContext = BreadcrumbsContext & {
  certificateId: string;
};

type SlotBreadcrumbsContext = ProjectBreadcrumbsContext & {
  slotId: string;
  slotLabel: string;
};

type ReportBreadcrumbsContext = SlotBreadcrumbsContext & {
  reportId: string;
  reportLabel: string;
};

type IntegrationBreadcrumbContext = BreadcrumbsContext & {
  integrationName: string;
  integrationPath: string;
};

/**
 * BreadcrumbFactory contains all getters and helpers for generating breadcrumbs.
 * Allows us to reuse the configurations from other pages where it is logical
 */
export class BreadcrumbFactory {
  getDashboardBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.DASHBOARD), path: PATHS.ORGANIZATION_DASHBOARD },
    ];
  }

  getAccessibilityStatementBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t('accessibilityStatement.title'), path: PATHS.ACCESSIBILITY_STATEMENT },
    ];
  }

  getUseConditionsStatementBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t('useConditionsStatement.title'), path: PATHS.USE_CONDITIONS_STATEMENT },
    ];
  }

  getProjectsBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.PROJECTS), path: PATHS.ORGANIZATION_PROJECTS },
    ];
  }

  getProjectBreadcrumbs({ projectId, projectLabel, nestedTabs }: ProjectBreadcrumbsContext): Breadcrumb[] {
    return this.buildPaths(
      [...this.getProjectsBreadcrumbs(), { name: projectLabel, path: PATHS.PROJECT_DASHBOARD, shrink: true }, ...this.getCurrentPath(nestedTabs)],
      { projectId }
    );
  }

  getReportBreadcrumbs({ projectId, projectLabel, slotId, reportId, reportLabel, nestedTabs }: ReportBreadcrumbsContext): Breadcrumb[] {
    const reportBreadcrumbs = [
      { name: t(TabNameLocalizationKeys.REPORTS), path: PATHS.PROJECT_SLOTS },
      { name: reportLabel, path: PATHS.REPORT, shrink: true },
    ];

    return this.buildPaths([...this.getProjectBreadcrumbs({ projectId, projectLabel }), ...reportBreadcrumbs, ...this.getCurrentPath(nestedTabs)], {
      projectId,
      slotId,
      reportId,
    });
  }

  getUsersBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.USERS), path: PATHS.USERS },
    ];
  }

  getScansBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.SCANS), path: PATHS.SCANS },
    ];
  }

  getIntegrationsBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.INTEGRATIONS), path: PATHS.INTEGRATIONS },
    ];
  }

  getIntegrationBreadcrumbs({ integrationName, integrationPath }: IntegrationBreadcrumbContext): Breadcrumb[] {
    return [...this.getIntegrationsBreadcrumbs(), { name: integrationName, path: integrationPath }];
  }

  getProfilesBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.PROFILES), path: PATHS.PROFILES },
    ];
  }

  getProfileBreadcrumb({ path, label }: ProfileBreadcrumbsContext): Breadcrumb[] {
    return [...this.getProfilesBreadcrumbs(), { name: label, path, shrink: true }];
  }

  getKeysBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.KEYS), path: PATHS.KEYS },
    ];
  }

  getKeyBreadcrumbs({ keyId }: KeyBreadcrumbsContext): Breadcrumb[] {
    return [
      ...this.getKeysBreadcrumbs(),
      {
        name: t('keyWithId', { keyId }),
        path: UrlHelper.generatePath(PATHS.KEY, { keyId }),
        shrink: true,
      },
    ];
  }

  getCertificatesBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.CERTIFICATES), path: PATHS.CERTIFICATES },
    ];
  }

  getCertificateBreadcrumbs({ certificateId }: CertificateBreadcrumbsContext): Breadcrumb[] {
    return [
      ...this.getCertificatesBreadcrumbs(),
      {
        name: t('certificateWithId', { certificateId }),
        path: UrlHelper.generatePath(PATHS.CERTIFICATE, { certificateId }),
        shrink: true,
      },
    ];
  }

  getSensorsBreadcrumbs(): Breadcrumb[] {
    return [
      { name: t(TabNameLocalizationKeys.HOME), path: PATHS.ORGANIZATION_DASHBOARD },
      { name: t(TabNameLocalizationKeys.SENSORS), path: PATHS.SENSORS },
    ];
  }

  /**
   * getCurrentPath takes the tabs for a view and returns whichever tab matches
   * so we can append it to the end of the breadcrumbs array.
   * @param {Route[]} nestedTabs - Nested tabs defined for a view that has sub navigation.
   * @returns {Breadcrumb[]}
   */
  getCurrentPath(nestedTabs?: Route[]): Breadcrumb[] {
    const currentPath = nestedTabs?.find((route) => {
      const hasMatchingChildren = route?.nestedPaths?.find((childPath: string) => UrlHelper.isCurrentPath(childPath));

      return UrlHelper.isCurrentPath(route.path) || hasMatchingChildren;
    });

    if (currentPath) {
      return [{ ...currentPath }] as Breadcrumb[];
    }

    return [];
  }

  private buildPaths(breadcrumbs: Breadcrumb[], params: Params): Breadcrumb[] {
    return breadcrumbs.map((b) => ({ ...b, path: UrlHelper.generatePath(b.path, params) }));
  }
}
